import { supabase } from "./supabase";

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:8787";

/** An /admin API failure with a message fit to show staff. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    /** The JSON error body, for routes that return more detail (e.g. a list of problems). */
    readonly body?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const FALLBACK: Record<number, string> = {
  401: "Your session has ended. Sign in again.",
  403: "Your role cannot do this.",
  404: "Not found.",
  429: "Too many requests. Wait a minute and try again.",
  502: "The server could not finish this. Try again.",
  503: "The service is busy right now. Wait a minute and try again.",
  504: "The server took too long to respond. Try again.",
};

export interface AdminRequestOptions {
  /** Abort after this long (ms). Default 30 s. */
  timeoutMs?: number;
}

async function apiRequest(base: "/admin" | "/crew", path: string, init: RequestInit, { timeoutMs = 30_000 }: AdminRequestOptions): Promise<Response> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  let res: Response;
  try {
    // Multipart uploads (FormData) set their own content type with the boundary.
    const json = init.body !== undefined && !(init.body instanceof FormData);
    res = await fetch(`${API_URL}${base}${path}`, {
      ...init,
      signal: ctrl.signal,
      headers: { ...(json ? { "content-type": "application/json" } : {}), ...(token ? { authorization: `Bearer ${token}` } : {}), ...init.headers },
    });
  } catch (e) {
    if (ctrl.signal.aborted) throw new ApiError(0, "This is taking too long. Try again in a minute.", "timeout");
    throw new ApiError(0, "Could not reach the server. Check your connection and try again.", "network");
  } finally {
    clearTimeout(timer);
  }
  if (!res.ok) {
    let body: { error?: string; message?: string; [k: string]: unknown } = {};
    try {
      body = (await res.json()) as typeof body;
    } catch {
      /* not JSON */
    }
    throw new ApiError(res.status, body.message ?? FALLBACK[res.status] ?? `Request failed (${res.status}).`, body.error, body);
  }
  return res;
}

/** Call an /admin route on the API Worker with the current Supabase access token. */
export async function adminFetch<T>(path: string, init: RequestInit = {}, opts: AdminRequestOptions = {}): Promise<T> {
  return (await (await apiRequest("/admin", path, init, opts)).json()) as T;
}

/** Call a /crew route (job packs for crew) on the API Worker. */
export async function crewFetch<T>(path: string, init: RequestInit = {}, opts: AdminRequestOptions = {}): Promise<T> {
  return (await (await apiRequest("/crew", path, init, opts)).json()) as T;
}

/** Fetch a binary /crew resource (a job document). */
export async function crewBlob(path: string, opts: AdminRequestOptions = {}): Promise<Blob> {
  return (await apiRequest("/crew", path, {}, opts)).blob();
}

/** Fetch a binary /admin resource (for example a PDF). */
export async function adminBlob(path: string, init: RequestInit = {}, opts: AdminRequestOptions = {}): Promise<Blob> {
  return (await apiRequest("/admin", path, init, opts)).blob();
}
